import VTDocumentViewer, { useDocumentViewer } from "components/DocumentViewer";
import { FileSearch } from "lucide-react";
import { bool, func, number, shape, string } from "prop-types";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useSelector } from "react-redux";
import {
  selectDocumentParagraphs,
  selectPageIntros,
} from "slices/documentViewer/selectors";
import fetchBinaryDataByUrl from "utils/fetchBinaryDataByUrl";

import styles from "./CoverageDocumentPane.module.scss";

const MIME_TO_EXTENSION = {
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/msword": "doc",
};

const extensionFromMimeType = (mimeType) => {
  const [type] = (mimeType || "").split(";");
  return MIME_TO_EXTENSION[type.trim()] || "";
};

const CoverageDocumentPane = forwardRef(
  (
    {
      documentId = null,
      target = null,
      documentLabel = "",
      isDrawer = false,
      isNarrow = false,
      onToggleNarrow,
    },
    forwardedRef
  ) => {
    const [file, setFile] = useState(null);
    const [documentName, setDocumentName] = useState("");

    const { scrollViewerToLocation, viewerRef } =
      useDocumentViewer(documentName);
    const documentParagraphs = useSelector(selectDocumentParagraphs);
    const pageIntros = useSelector(selectPageIntros);

    const sourceDocumentId = documentId;
    const {
      text: targetText,
      pageNumber,
      paragraphDistance = 0,
    } = target || {};

    useImperativeHandle(forwardedRef, () => ({
      scrollViewerToLocation,
      viewer: viewerRef.current,
    }));

    useEffect(() => {
      let cancelled = false;
      setFile(null);
      setDocumentName("");

      if (sourceDocumentId == null) {
        return undefined;
      }

      (async () => {
        try {
          const url = `/nextgen/document/${sourceDocumentId}/stream`;
          const baseName = documentLabel || `Document ${sourceDocumentId}`;
          const rawFile = await fetchBinaryDataByUrl(url, baseName);

          if (cancelled || !rawFile) {
            return;
          }

          const extension = extensionFromMimeType(rawFile.type);
          const name = extension ? `${baseName}.${extension}` : baseName;

          setDocumentName(name);
          setFile(new File([rawFile], name, { type: rawFile.type }));
        } catch {
          // Best-effort viewer — swallow. The header still renders.
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [sourceDocumentId, documentLabel]);

    // The viewer only has real content to scroll/search once its own load has
    // populated documentParagraphs (docx) or pageIntros (pdf) — the fetched File
    // being set just means the bytes arrived, not that they're parsed yet.
    const hasParsedContent =
      documentParagraphs.length > 0 || pageIntros.length > 0;

    useEffect(() => {
      if (!file || !hasParsedContent || !targetText) {
        return;
      }
      // paragraphDistance is a 0–1 position in the document, NOT a paragraph index
      // — see paragraphDistanceFor in slices/proposalOutline/coverage.
      //
      // No full-page mask: trace navigation scrolls this pane on every section click and
      // every stepper press, and blocking the whole page each time reads as a flash.
      scrollViewerToLocation({
        location: pageNumber,
        paragraphDistance,
        text: targetText,
        showFullPageMask: false,
      });
    }, [file, hasParsedContent, targetText, pageNumber, paragraphDistance]);

    return (
      <div
        className={styles.pane}
        data-testid="coverage-document-pane"
        aria-label="Source document"
        role="region"
      >
        <div className={styles.body}>
          {documentId !== null ? (
            <VTDocumentViewer
              canToggleNarrow
              file={file}
              isNarrow={isNarrow}
              onToggleNarrow={onToggleNarrow}
              ref={viewerRef}
              showFileName={isDrawer}
              showNavigation={false}
              toolbarClassName={!isDrawer && styles.toolbar}
            />
          ) : (
            <div className={styles.placeholder}>
              <FileSearch size={24} aria-hidden />
              <span>No source document to preview.</span>
            </div>
          )}
        </div>
      </div>
    );
  }
);

CoverageDocumentPane.displayName = "CoverageDocumentPane";

CoverageDocumentPane.propTypes = {
  documentId: number,
  documentLabel: string,
  isDrawer: bool,
  isNarrow: bool,
  onToggleNarrow: func.isRequired,
  target: shape({
    pageNumber: number,
    paragraphDistance: number,
    text: string,
  }),
};

export default CoverageDocumentPane;
